'use client';

import { useState, useRef } from 'react';
import Link from 'next/link';
import { format } from 'date-fns';
import { Loader2, ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import { useCustomQuery } from '@/lib/hooks/useCustomQuery';
import { activitiesApi } from '@/lib/api/endpoints';
import { useUrlSync } from '@/lib/hooks/useUrlSync';
import { MultiSelectDropdown } from '@/components/ui/multi-select-dropdown';
import { DatePickerWithRange } from '@/components/custom/date-picker-with-range';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ACTIVITY_TYPE_OPTIONS } from '@sizzlenz/shared';
import type { DateRange } from 'react-day-picker';
import type { Activity } from '@/lib/api/types';

const ACTIVITY_TYPE_FILTER_OPTIONS = ACTIVITY_TYPE_OPTIONS.map((opt) => ({
  id: opt.value,
  label: opt.label,
}));

const URL_SYNC_FILTERS = {
  types: { paramName: 'types', defaultValue: [] as string[], isArray: true as const },
  startDate: { paramName: 'startDate', defaultValue: '' },
  endDate: { paramName: 'endDate', defaultValue: '' },
};

/**
 * Detail level controls how much information is shown per activity row.
 * - 'compact': Just the action description + object link + time
 * - 'normal': Above + content preview (comment text, etc.)
 * - 'expanded': Above + full data payload (email subject, message, vote entities, etc.)
 */
export type ActivityDetailLevel = 'compact' | 'normal' | 'expanded';

interface ActivityViewProps {
  entityId: number;
  initiativeId?: string;
  userId?: string;
  /** Controls how much detail is shown per row. Defaults to 'normal'. */
  detailLevel?: ActivityDetailLevel;
}

/**
 * ActivityView — paginated activity log with date range and type filters.
 * Displays a feed similar to the screenshot reference (Facebook Activity Log style).
 */
export function ActivityView({
  entityId,
  initiativeId,
  userId,
  detailLevel = 'normal',
}: ActivityViewProps) {
  const { values, handlers, isReady } = useUrlSync({ filters: URL_SYNC_FILTERS });
  const [page, setPage] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const limit = 20;

  const dateRange: DateRange | undefined =
    values.startDate && values.endDate
      ? { from: new Date(values.startDate), to: new Date(values.endDate) }
      : undefined;

  const { data, isLoading } = useCustomQuery({
    queryKey: [
      'entity-feed',
      entityId,
      values.types,
      values.startDate,
      values.endDate,
      userId,
      initiativeId,
      page,
    ],
    queryFn: () =>
      activitiesApi.getEntityFeed(entityId, {
        limit,
        offset: page * limit,
        types: values.types.length > 0 ? values.types : undefined,
        startDate: values.startDate || undefined,
        endDate: values.endDate || undefined,
        userId,
        initiativeId,
      }),
    enabled: isReady && !!entityId,
  });

  const items = data?.items ?? [];
  const pagination = data?.pagination;
  const hasMore = pagination?.hasMore ?? false;

  const handleDateRangeChange = (range: DateRange | undefined) => {
    if (range?.from) {
      handlers.startDate(range.from.toISOString());
    } else {
      handlers.startDate('');
    }
    if (range?.to) {
      handlers.endDate(range.to.toISOString());
    } else {
      handlers.endDate('');
    }
    setPage(0);
  };

  const handleTypesChange = (types: string[]) => {
    handlers.types(types);
    setPage(0);
  };

  // Group activities by date
  const groupedActivities = groupByDate(items);

  if (!isReady) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div ref={containerRef} className="space-y-6">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <DatePickerWithRange date={dateRange} setDate={handleDateRangeChange} clearable />
        <MultiSelectDropdown
          label={null}
          placeholder="Activity Type"
          options={ACTIVITY_TYPE_FILTER_OPTIONS}
          selectedIds={values.types}
          onSelectionChange={handleTypesChange}
          showSummaryInTrigger
        />
      </div>

      {/* Feed */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground text-sm">
          No activity found for the selected filters.
        </div>
      ) : (
        <div className="space-y-6">
          {groupedActivities.map(({ dateLabel, activities }) => (
            <div key={dateLabel}>
              <h3 className="text-sm font-semibold text-gray-700 mb-3 sticky top-0 bg-gray-50 py-1">
                {dateLabel}
              </h3>
              <div className="space-y-2">
                {activities.map((item) => (
                  <ActivityRow key={item.id} activity={item} detailLevel={detailLevel} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {(page > 0 || hasMore) && (
        <div className="flex items-center justify-between pt-4 border-t">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 0}
            onClick={() => {
              setPage((p) => Math.max(0, p - 1));
              containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
            className="cursor-pointer"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            {pagination
              ? `${pagination.offset + 1}–${pagination.offset + items.length} of ${pagination.total}`
              : ''}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={!hasMore}
            onClick={() => {
              setPage((p) => p + 1);
              containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
            className="cursor-pointer"
          >
            Next
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      )}
    </div>
  );
}

/** Individual activity row */
function ActivityRow({
  activity,
  detailLevel = 'normal',
}: {
  activity: Activity & {
    user?: { id: number; name: string | null; email: string; image: string | null };
    objectName?: string;
  };
  detailLevel?: ActivityDetailLevel;
}) {
  const [expanded, setExpanded] = useState(false);
  const userName = activity.user?.name || activity.user?.email || 'Unknown';
  const time = format(new Date(activity.createdAt), 'HH:mm');
  const description = getActivityDescription(activity);
  const data = activity.data as Record<string, unknown> | null;
  const isDeleted = !!activity.deletedAt;

  // Determine if this row has expandable detail (entities, note, etc.)
  const hasExpandableDetail =
    data && (data.note || (Array.isArray(data.entities) && data.entities.length > 0));

  const showDetail = detailLevel === 'expanded' || expanded;

  return (
    <div
      className={`flex items-start gap-3 rounded-lg border p-3 shadow-sm bg-white ${hasExpandableDetail ? 'cursor-pointer' : ''}`}
      onClick={hasExpandableDetail ? () => setExpanded((v) => !v) : undefined}
    >
      {/* Avatar */}
      <div className="shrink-0">
        {activity.user?.image ? (
          <img
            src={activity.user.image}
            alt={userName}
            className="h-8 w-8 rounded-full object-cover"
          />
        ) : (
          <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium text-gray-600">
            {userName.charAt(0).toUpperCase()}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className="text-sm">
          <span className="font-medium text-gray-900">{userName}</span>{' '}
          <span className="text-gray-600">{description}</span>
          {isDeleted && (
            <Badge variant="destructive" className="ml-1.5 text-[10px] px-1.5 py-0">
              Withdrawn {format(new Date(activity.deletedAt!), 'd MMM')}
            </Badge>
          )}
          {hasExpandableDetail && !showDetail && (
            <ChevronRight className="inline-block w-3.5 h-3.5 ml-1 text-gray-400" />
          )}
          {hasExpandableDetail && showDetail && (
            <ChevronDown className="inline-block w-3.5 h-3.5 ml-1 text-gray-400" />
          )}
        </p>

        {/* Normal+ : show content preview */}
        {detailLevel !== 'compact' && activity.content && (
          <p className="mt-1 text-sm text-gray-500 line-clamp-2">{activity.content}</p>
        )}

        {/* Expanded / toggled: show structured data payload (entities, note, etc.) */}
        {showDetail && data && <ActivityDataDetail data={data} type={activity.type} />}

        <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
          <span className="capitalize rounded bg-gray-100 px-1.5 py-0.5">
            {activity.objectType}
          </span>
          <span>·</span>
          <ObjectLink
            objectType={activity.objectType}
            objectId={activity.objectId}
            objectName={(activity as any).objectName}
          />
        </div>
      </div>

      {/* Time */}
      <span className="text-xs text-muted-foreground shrink-0">{time}</span>
    </div>
  );
}

/** Renders a linked object name pointing to the correct route */
function ObjectLink({
  objectType,
  objectId,
  objectName,
}: {
  objectType: string;
  objectId: string;
  objectName?: string;
}) {
  const label = objectName || objectId;
  const href = getObjectHref(objectType, objectId);

  if (href) {
    return (
      <Link
        href={href}
        className="font-medium text-gray-700 hover:text-gray-900 hover:underline cursor-pointer"
      >
        {label}
      </Link>
    );
  }

  return <span className="font-medium text-gray-700">{label}</span>;
}

/** Renders expanded detail from the activity data payload */
function ActivityDataDetail({ data, type }: { data: Record<string, unknown>; type: string }) {
  const entries: Array<{ label: string; value: string }> = [];

  // Email-related fields
  if (data.subject) entries.push({ label: 'Subject', value: String(data.subject) });
  if (data.message) entries.push({ label: 'Message', value: String(data.message) });
  if (data.recipients) {
    const recipients = Array.isArray(data.recipients)
      ? data.recipients.map((r: any) => r.email || r.name || r).join(', ')
      : String(data.recipients);
    entries.push({ label: 'Recipients', value: recipients });
  }

  // Vote-related
  if (data.vote) entries.push({ label: 'Vote', value: String(data.vote) });
  if (data.note) entries.push({ label: 'Note', value: String(data.note) });
  if (data.entities && Array.isArray(data.entities)) {
    const names = data.entities.map((e: any) => e.name || e.id).join(', ');
    entries.push({ label: 'Representing', value: names });
  }

  // Reaction
  if (data.reaction_type) entries.push({ label: 'Reaction', value: String(data.reaction_type) });

  // Generic: show any remaining string/number fields not already shown
  const shownKeys = new Set([
    'subject',
    'message',
    'recipients',
    'vote',
    'note',
    'entities',
    'reaction_type',
    'email', // Skip raw HTML email body — message field has the plain text
    'connectionIds', // Skip internal IDs
  ]);
  for (const [key, val] of Object.entries(data)) {
    if (shownKeys.has(key)) continue;
    if (typeof val === 'string' || typeof val === 'number') {
      entries.push({ label: key.replace(/_/g, ' '), value: String(val) });
    }
  }

  if (entries.length === 0) return null;

  return (
    <div className="mt-1.5 space-y-0.5 text-xs border-l-2 border-gray-200 pl-2">
      {entries.map(({ label, value }) => (
        <div key={label} className="flex gap-1.5">
          <span className="text-muted-foreground capitalize shrink-0">{label}:</span>
          <span className="text-gray-700 line-clamp-2">{value}</span>
        </div>
      ))}
    </div>
  );
}

/** Map objectType + objectId to an app route */
function getObjectHref(objectType: string, objectId: string): string | null {
  switch (objectType) {
    case 'entity':
      return `/entities/${objectId}`;
    case 'initiative':
      return `/initiatives/${objectId}`;
    case 'item':
      return `/items/${objectId}`;
    case 'collection':
      return `/collections/${objectId}`;
    case 'content':
      return `/content/${objectId}`;
    default:
      return null;
  }
}

/** Helper to get a human-readable description of an activity */
function getActivityDescription(activity: Activity): string {
  switch (activity.type) {
    case 'comment':
      return 'left a comment';
    case 'reaction': {
      const reactionType = (activity.data as any)?.reaction_type;
      return reactionType ? `reacted with ${reactionType}` : 'reacted';
    }
    case 'vote': {
      const vote = (activity.data as any)?.vote;
      if (vote === 'interested') return 'expressed interest';
      return vote ? `voted ${vote}` : 'voted';
    }
    default:
      return (activity.type as string).replace(/_/g, ' ');
  }
}

/** Group activities by date */
function groupByDate(
  activities: Array<Activity & { user?: any }>
): Array<{ dateLabel: string; activities: typeof activities }> {
  const groups = new Map<string, typeof activities>();

  for (const item of activities) {
    const dateKey = format(new Date(item.createdAt), 'd MMMM yyyy');
    if (!groups.has(dateKey)) {
      groups.set(dateKey, []);
    }
    groups.get(dateKey)!.push(item);
  }

  return Array.from(groups.entries()).map(([dateLabel, activities]) => ({
    dateLabel,
    activities,
  }));
}
